import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right';
  hideBelow?: 'sm' | 'md' | 'lg';
  colSpan?: number;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  rowHighlight?: (row: T) => string | undefined;
  footer?: ReactNode;
}

const HIDE_CLASSES: Record<NonNullable<Column<unknown>['hideBelow']>, string> = {
  sm: 'hidden sm:block',
  md: 'hidden md:block',
  lg: 'hidden lg:block',
};

function colSpanClass(span: number | undefined) {
  switch (span) {
    case 1:
      return 'col-span-1';
    case 2:
      return 'col-span-2';
    case 3:
      return 'col-span-3';
    case 4:
      return 'col-span-4';
    case 5:
      return 'col-span-5';
    case 6:
      return 'col-span-6';
    case 7:
      return 'col-span-7';
    case 8:
      return 'col-span-8';
    case 9:
      return 'col-span-9';
    case 10:
      return 'col-span-10';
    case 11:
      return 'col-span-11';
    case 12:
      return 'col-span-12';
    default:
      return 'col-span-1';
  }
}

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  rowHighlight,
  footer,
}: DataTableProps<T>) {
  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      <div className="grid grid-cols-12 gap-3 px-5 py-2.5 hair-b bg-wash text-[11px] font-semibold text-ink-4 uppercase tracking-wider">
        {columns.map((col) => {
          const span = colSpanClass(col.colSpan);
          const align = col.align === 'right' ? 'text-right' : '';
          const hide = col.hideBelow ? HIDE_CLASSES[col.hideBelow] : '';
          return (
            <div
              key={col.key}
              className={`${span} ${align} ${hide} flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}
            >
              <span>{col.header}</span>
            </div>
          );
        })}
      </div>

      <div className="divide-hair">
        {data.map((row, rowIdx) => {
          const highlight = rowHighlight ? rowHighlight(row) : '';
          return (
            <div
              key={rowIdx}
              onClick={() => onRowClick?.(row)}
              className={`grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-paper-2 transition cursor-pointer ${highlight ?? ''}`}
            >
              {columns.map((col) => {
                const span = colSpanClass(col.colSpan);
                const align = col.align === 'right' ? 'text-right' : '';
                const hide = col.hideBelow ? HIDE_CLASSES[col.hideBelow] : '';
                return (
                  <div key={col.key} className={`${span} ${align} ${hide} min-w-0`}>
                    {col.render ? col.render(row) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {footer && (
        <div className="px-5 py-3 hair-t bg-wash text-[12px] text-ink-3">
          {footer}
        </div>
      )}
    </div>
  );
}
